import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserInfo } from '../types';

interface UseAuthReturn {
  user: UserInfo | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string, user: UserInfo) => void;
  logout: () => void;
}

/**
 * Custom hook for managing authentication state
 * Handles login, logout, and token persistence in localStorage
 * 
 * @returns Authentication state and methods
 */
export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const navigate = useNavigate();

  // Load authentication state from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('userInfo');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser) as UserInfo;
        setToken(storedToken);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user info:', error);
        // Clear invalid data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
      }
    }
  }, []);

  /**
   * Login user and persist authentication data
   * @param token - Authentication token from backend
   * @param user - User information from backend
   */
  const login = (token: string, user: UserInfo) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('userInfo', JSON.stringify(user));
    setToken(token);
    setUser(user);
  };

  /**
   * Logout user and clear authentication data
   * Redirects to login page after clearing state
   */
  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    setToken(null);
    setUser(null);
    navigate('/login');
  };

  return {
    user,
    token,
    isAuthenticated: !!token,
    login,
    logout,
  };
};
